import { Button, Card, CardBody, CardHeader, Select, SelectItem, Textarea } from '@nextui-org/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import {
  ConnectorAnnotation,
  getAnnotationFields,
  saveAnnotationFields
} from '~/services/annotationService'
import { QueryKeys } from '~/services/queryClient'
import { BetterToast } from '~/utils/toast'
import { DocMarkdown } from '../documents'

export function AnnotationForm() {
  const [selectedConnector, setSelectedConnector] = useState('')
  const [selectedVersion, setSelectedVersion] = useState('')

  const [formData, setFormData] = useState<ConnectorAnnotation>({
    pom_dep: '',
    config_code: '',
    action_code: '',
    action_type: 'source',
    action_description: ''
  })

  const [errors, setErrors] = useState({
    config_code: false,
    action_type: false,
    action_code: false,
    action_description: false
  })

  const { data: annotationTasks = { tasks: [] }, isLoading } = useQuery({
    queryKey: QueryKeys.AnnotationTasks,
    queryFn: getAnnotationFields,
    select: data => data || { tasks: [] }
  })

  const connectors = useMemo(() => {
    const uniqueConnectors = new Set(annotationTasks.tasks.map(task => task.connector))
    return Array.from(uniqueConnectors)
  }, [annotationTasks.tasks])

  const versions = useMemo(() => {
    if (!selectedConnector) return []
    const uniqueVersions = new Set(
      annotationTasks.tasks
        .filter(task => task.connector === selectedConnector)
        .map(task => task.version)
    )
    return Array.from(uniqueVersions)
  }, [selectedConnector, annotationTasks.tasks])

  const pomDep = useMemo(() => {
    if (!selectedConnector || !selectedVersion) return ''
    const task = annotationTasks.tasks.find(
      task => task.connector === selectedConnector && task.version === selectedVersion
    )
    return task?.pomDep || ''
  }, [selectedConnector, selectedVersion, annotationTasks.tasks])

  const keysList = useMemo(() => {
    if (!selectedConnector || !selectedVersion) return []
    return annotationTasks.tasks
      .filter(task => task.connector === selectedConnector && task.version === selectedVersion)
      .map(task => ({
        configKey: task.configKey,
        key: 'opKey' in task ? task.opKey : task.srcKey,
        type: 'opKey' in task ? 'operation' : 'source'
      }))
  }, [selectedConnector, selectedVersion, annotationTasks.tasks])

  const { mutateAsync: saveAnnotation, isPending } = useMutation({
    mutationFn: saveAnnotationFields,
    onSuccess: () => {
      setFormData({
        pom_dep: pomDep, // Keep the pomDep when resetting
        config_code: '',
        action_code: '',
        action_type: 'source',
        action_description: ''
      })
    }
  })

  const handleConnectorChange = (value: string) => {
    setSelectedConnector(value)
    setSelectedVersion('')
    setFormData(prev => ({
      ...prev,
      pom_dep: '',
      config_code: ''
    }))
  }

  const handleVersionChange = (value: string) => {
    setSelectedVersion(value)
    setFormData(prev => ({
      ...prev,
      pom_dep: pomDep,
      config_code: ''
    }))
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setErrors(prev => ({
      ...prev,
      [field]: false
    }))
  }

  const validateForm = () => {
    const newErrors = {
      config_code: !formData.config_code,
      action_type: !formData.action_type,
      action_code: !formData.action_code,
      action_description: !formData.action_description
    }
    setErrors(newErrors)
    return !Object.values(newErrors).some(error => error)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      try {
        await saveAnnotation({
          ...formData,
          pom_dep: pomDep // Ensure we're using the current pomDep
        })
        BetterToast.success('Annotation saved')
      } catch (e) {
        BetterToast.error({
          message: (e as Error).message,
          duration: 5000
        })
      }
    }
  }

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-screen select-none'>
        Loading annotation fields...
      </div>
    )
  }

  return (
    <div className='flex h-screen w-screen p-6 gap-6'>
      {/* Left Sidebar */}
      <div className='w-1/2 flex flex-col gap-6'>
        {/* Connector & Version Selection Panel */}
        <Card className='flex-none'>
          <CardHeader className='px-6 py-4'>
            <h3 className='text-lg font-semibold'>Connector Selection</h3>
          </CardHeader>
          <CardBody className='px-6 pb-6'>
            <div className='space-y-4'>
              <Select
                label='Connector'
                placeholder='Select connector'
                selectedKeys={selectedConnector ? [selectedConnector] : []}
                onChange={e => handleConnectorChange(e.target.value)}
              >
                {connectors.map(connector => (
                  <SelectItem key={connector} value={connector}>
                    {connector}
                  </SelectItem>
                ))}
              </Select>

              <Select
                label='Version'
                placeholder='Select version'
                selectedKeys={selectedVersion ? [selectedVersion] : []}
                onChange={e => handleVersionChange(e.target.value)}
                isDisabled={!selectedConnector}
              >
                {versions.map(version => (
                  <SelectItem key={version} value={version}>
                    {version}
                  </SelectItem>
                ))}
              </Select>

              {pomDep && (
                <div className='flex flex-col gap-2'>
                  <div className='text-sm'>POM Dependency</div>
                  <DocMarkdown text={`\`\`\`xml\n${pomDep}\n\`\`\``} className='pl-0' />
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Keys List Panel */}
        <Card className='flex-1'>
          <CardHeader className='px-6 py-4'>
            <h3 className='text-lg font-semibold'>Available Keys</h3>
          </CardHeader>
          <CardBody className='p-0'>
            <div className='h-full overflow-y-auto'>
              <table className='w-full'>
                <thead className='sticky top-0 bg-white shadow-sm'>
                  <tr className='border-b'>
                    <th className='text-left p-4'>Config Key</th>
                    <th className='text-left p-4'>Key</th>
                    <th className='text-left p-4'>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {keysList.map((item, index) => (
                    <tr
                      key={index}
                      className='border-b hover:bg-gray-50 cursor-pointer transition-colors'
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          action_type: item.type as 'source' | 'operation'
                        }))
                      }}
                    >
                      <td className='p-4'>{item.configKey}</td>
                      <td className='p-4'>{item.key}</td>
                      <td className='p-4'>{item.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Main Form Area */}
      <Card className='flex-1'>
        <CardHeader className='px-6 py-4'>
          <h3 className='text-lg font-semibold'>Annotation Details</h3>
        </CardHeader>
        <CardBody className='px-6'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            <Select
              label='Action Type'
              placeholder='Select action type'
              selectedKeys={formData.action_type ? [formData.action_type] : []}
              isInvalid={errors.action_type}
              errorMessage={errors.action_type && 'Action type is required'}
              onChange={e => handleChange('action_type', e.target.value)}
            >
              <SelectItem key='source' value='source'>
                Source
              </SelectItem>
              <SelectItem key='operation' value='operation'>
                Operation
              </SelectItem>
            </Select>

            <Textarea
              label='Config Code'
              placeholder='Enter config code'
              value={formData.config_code}
              isInvalid={errors.config_code}
              errorMessage={errors.config_code && 'Config code is required'}
              onChange={e => handleChange('config_code', e.target.value)}
              minRows={20}
            />

            <Textarea
              label='Action Code'
              placeholder='Enter action code'
              value={formData.action_code}
              isInvalid={errors.action_code}
              errorMessage={errors.action_code && 'Action code is required'}
              onChange={e => handleChange('action_code', e.target.value)}
              minRows={20}
            />

            <Textarea
              label='Action Description'
              placeholder='Enter action description'
              value={formData.action_description}
              isInvalid={errors.action_description}
              errorMessage={errors.action_description && 'Action description is required'}
              onChange={e => handleChange('action_description', e.target.value)}
              minRows={5}
            />

            <div className='flex justify-end pt-4'>
              <Button type='submit' color='primary' size='lg' isLoading={isPending}>
                Save Annotation
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}

export default AnnotationForm
